/* @jsx h */
import { defineComponent, h, ref, onBeforeUnmount, watch, computed } from "vue";
import { AddressDisplay } from "./AddressDisplay";
import {
  buildServerURL,
  searchGeolocations,
  type GeolocationSearchResult,
} from "./utils/geolocation";

export type SearchResult = GeolocationSearchResult & {
  email: string;
  phone: string;
};

export interface CustomerAddressSearchProps {
  onApplyResult: (result: SearchResult) => void;
  onCancel: () => void;
}

export const CustomerAddressSearch =
  defineComponent<CustomerAddressSearchProps>({
    name: "CustomerAddressSearch",
    props: ["onApplyResult", "onCancel"],
    setup(props) {
      const searchQuery = ref("");
      const isSearching = ref(false);
      const searchResults = ref<SearchResult[]>([]);
      const searchError = ref("");
      const selectedResult = ref<SearchResult | null>(null);
      const debounceTimer = ref<ReturnType<typeof setTimeout> | null>(null);
      const abortController = ref<AbortController | null>(null);

      const selectedPreview = computed(
        () =>
          searchResults.value.find(
            (result) => result.id === selectedResult.value?.id,
          ) || null,
      );

      const performSearch = async (query: string) => {
        const trimmed = query.trim();
        if (!trimmed) {
          searchResults.value = [];
          selectedResult.value = null;
          searchError.value = "";
          isSearching.value = false;
          if (abortController.value) {
            abortController.value.abort();
            abortController.value = null;
          }
          return;
        }

        if (abortController.value) {
          abortController.value.abort();
        }
        const controller = new AbortController();
        abortController.value = controller;

        isSearching.value = true;
        searchError.value = "";
        try {
          const baseResults = await searchGeolocations(trimmed, {
            limit: 5,
            country: "CH",
            proximity: "ip",
            //            {
            //              lat: "46.2044",
            //              lon: "6.1432",
            //            },
            types: ["street", "address"],
            signal: controller.signal,
          });

          const results: SearchResult[] = baseResults.map((result) => ({
            ...result,
            email: "",
            phone: "",
          }));

          searchResults.value = results;

          if (results.length > 0) {
            selectedResult.value = results[0];
          } else if (
            !results.find((result) => result.id === selectedResult.value?.id)
          ) {
            selectedResult.value = null;
          }
        } catch (error) {
          if (error instanceof DOMException && error.name === "AbortError") {
            return;
          }
          console.error("Mapbox address search failed:", error);
          searchError.value =
            "We couldn't fetch suggestions right now. Please try again.";
        } finally {
          if (abortController.value === controller) {
            abortController.value = null;
          }
          isSearching.value = false;
        }
      };

      const scheduleSearch = (query: string) => {
        if (debounceTimer.value) {
          clearTimeout(debounceTimer.value);
        }
        debounceTimer.value = setTimeout(() => {
          performSearch(query);
        }, 600);
      };

      const handleInput = (event: Event) => {
        const value = (event.target as HTMLInputElement).value;
        searchQuery.value = value;
        scheduleSearch(value);
      };

      const handleSelectResult = (result: SearchResult) => {
        selectedResult.value = result;
      };

      const handleApplyResult = () => {
        if (!selectedResult.value) return;
        const result = searchResults.value.find(
          (entry) => entry.id === selectedResult.value?.id,
        );
        if (!result) return;
        props.onApplyResult(result);
      };

      watch(
        () => searchQuery.value,
        (value) => {
          if (!value.trim()) {
            searchResults.value = [];
            selectedResult.value;
            searchError.value = "";
            if (debounceTimer.value) {
              clearTimeout(debounceTimer.value);
              debounceTimer.value = null;
            }
            if (abortController.value) {
              abortController.value.abort();
              abortController.value = null;
            }
            isSearching.value = false;
          }
        },
      );

      onBeforeUnmount(() => {
        if (debounceTimer.value) {
          clearTimeout(debounceTimer.value);
        }
        if (abortController.value) {
          abortController.value.abort();
        }
      });

      return () => (
        <div class="space-y-4">
          <div class="form-control">
            <label class="label">
              <span class="label-text font-medium">Search address</span>
              <span class="label-text-alt text-base-content/60">
                Powered by Mapbox
              </span>
            </label>
            <div class="join w-full">
              <input
                type="text"
                class="input input-bordered join-item grow"
                placeholder="e.g. Route des Acacias 60"
                value={searchQuery.value}
                onInput={handleInput}
              />
              <button
                class="btn join-item btn-ghost"
                disabled={!searchQuery.value}
                onClick={() => {
                  searchQuery.value = "";
                  searchResults.value = [];
                  selectedResult.value = null;
                }}
              >
                <span class="material-symbols-outlined">close</span>
              </button>
            </div>
          </div>

          {searchError.value && (
            <div class="alert alert-error">
              <span class="material-symbols-outlined">error</span>
              {searchError.value}
            </div>
          )}

          <div class="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
            <div class="space-y-3">
              {isSearching.value ? (
                <div class="flex items-center justify-center py-8">
                  <span class="loading loading-ring loading-lg text-primary" />
                </div>
              ) : searchResults.value.length ? (
                searchResults.value.map((result) => (
                  <button
                    key={result.id}
                    class={`card border transition-all w-full text-left ${
                      selectedResult.value?.id === result.id
                        ? "border-primary shadow-lg bg-primary/5"
                        : "border-base-300 hover:border-primary/60 hover:shadow"
                    }`}
                    onClick={() => {
                      if (selectedResult.value?.id === result.id) {
                        handleApplyResult();
                      } else {
                        handleSelectResult(result);
                      }
                    }}
                  >
                    <div class="card-body py-2 px-4 gap-2">
                      <div class="flex items-center gap-2 text-primary">
                        <span class="material-symbols-outlined text-base leading-none">
                          pin_drop
                        </span>
                        <span class="font-semibold truncate">
                          {result.label}
                        </span>
                      </div>
                      <AddressDisplay
                        street={result.street}
                        city={result.city}
                        postalCode={result.postal_code}
                        phone={result.phone}
                        email={result.email}
                        variant="compact"
                      />
                    </div>
                  </button>
                ))
              ) : (
                <div class="rounded-xl border border-dashed border-base-300 p-6 text-center space-y-2">
                  <span class="material-symbols-outlined text-4xl text-base-content/40">
                    travel_explore
                  </span>
                  <p class="text-sm text-base-content/70">
                    Start typing to see suggested addresses.
                  </p>
                </div>
              )}
            </div>
            <div class="rounded-2xl border border-base-300 bg-base-100 shadow-inner min-h-[260px] flex flex-col items-center justify-start text-center p-6 space-y-4">
              {selectedPreview.value ? (
                (() => {
                  const preview = selectedPreview.value!;
                  const staticUrl = buildServerURL({
                    longitude: preview.longitude,
                    latitude: preview.latitude,
                    label: preview.label.charAt(0),
                  });
                  return (
                    <>
                      <div class="flex gap-3">
                        <button
                          class="btn btn-ghost btn-sm"
                          onClick={props.onCancel}
                        >
                          Cancel
                        </button>
                        <button
                          class="btn btn-primary btn-sm"
                          onClick={handleApplyResult}
                        >
                          Use this address
                        </button>
                      </div>
                      {preview.street && !/\d/.test(preview.street) && (
                        <div class="alert alert-warning shadow-sm">
                          <span class="material-symbols-outlined text-lg">
                            warning
                          </span>
                          <span class="text-sm">
                            This address may be incomplete - no street number
                            detected
                          </span>
                        </div>
                      )}
                      <img
                        src={staticUrl}
                        alt={`Map preview of ${preview.label}`}
                        class="w-full h-48 rounded-xl object-cover shadow"
                      />
                      <div class="space-y-3 w-full">
                        <h4 class="font-semibold text-base-content text-left">
                          {preview.label}
                        </h4>
                        <AddressDisplay
                          street={preview.street}
                          city={preview.city}
                          postalCode={preview.postal_code}
                          phone={preview.phone}
                          email={preview.email}
                          variant="detailed"
                        />
                        <div class="pt-2 border-t border-base-300">
                          <p class="flex items-center gap-1.5 text-xs text-base-content/60">
                            <span class="material-symbols-outlined text-sm">
                              location_on
                            </span>
                            {preview.latitude.toFixed(5)},{" "}
                            {preview.longitude.toFixed(5)}
                          </p>
                        </div>
                      </div>
                    </>
                  );
                })()
              ) : (
                <>
                  <div class="rounded-full bg-base-300 text-base-content p-3">
                    <span class="material-symbols-outlined text-3xl">
                      satellite_alt
                    </span>
                  </div>
                  <h4 class="font-semibold text-base-content">
                    No address selected
                  </h4>
                  <p class="text-sm text-base-content/70 max-w-xs">
                    Choose a suggestion to preview details and apply it to the
                    order.
                  </p>
                  <button class="btn btn-ghost btn-sm" onClick={props.onCancel}>
                    Enter manually instead
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    },
  });
